import type { ClientSettingsHydraItem, ClientSettingsPatch } from "@verbleif/lib/src/composables/Api/Type/ClientSettings/client_settings.interface";
import { getQueryClient } from "@verbleif/lib/src/composables/Query/queryClient";
import { createGlobalState } from "@vueuse/core";
import { ref, watch } from "vue";
import { useLocationStore } from "../LocationStore/useLocationStore";
import { usePatchMutation, usePostMutation } from "../Mutation/useHydraMutations";
import { invalidateResource } from "../Query/invalidateResource";
import { resourceKeys, useResourceScope } from "../Query/resourceKeys";

export const useClientSettingsStore = createGlobalState(() => {
  const { selectedClient } = useLocationStore();
  const scope = useResourceScope();
  const current = ref<ClientSettingsHydraItem | null>(null);
  const isLoading = ref(false);
  const lastFetchedClient = ref<string | null>(null);
  const createSettings = usePostMutation<ClientSettingsHydraItem, ClientSettingsPatch & { client: string }>();
  const patchSettings = usePatchMutation<ClientSettingsHydraItem, ClientSettingsPatch>();

  function writeCurrent(data: ClientSettingsHydraItem): void {
    current.value = data;
    lastFetchedClient.value = selectedClient.value?.["@id"] ?? data.client;
    getQueryClient().setQueryData(resourceKeys.clientSettings.all(scope.value), data);
  }

  async function load(): Promise<void> {
    await invalidateResource(resourceKeys.clientSettings);
  }

  /**
   * Upserts the current client settings. When the backend returned the global
   * fallback (`global === true`), we POST a new client-specific row so the
   * next PATCH targets the right entity instead of mutating the shared
   * global defaults.
   */
  async function update(patch: ClientSettingsPatch): Promise<ClientSettingsHydraItem | null> {
    if (!current.value) {
      return null;
    }

    if (current.value.global) {
      const clientIri = selectedClient.value?.["@id"] ?? lastFetchedClient.value;
      if (!clientIri) {
        return null;
      }
      const data = await createSettings.mutateAsync({
        url: "/api/client_settings",
        body: {
          ...patch,
          client: clientIri,
        },
      });
      writeCurrent(data);
      return data;
    }

    const data = await patchSettings.mutateAsync({
      url: current.value["@id"],
      body: patch,
      config: { headers: { "Content-Type": "application/merge-patch+json" } },
    });
    writeCurrent(data);
    return data;
  }

  function reset() {
    current.value = null;
    lastFetchedClient.value = null;
  }

  watch(current, (value) => {
    if (value) {
      lastFetchedClient.value = selectedClient.value?.["@id"] ?? value.client;
    }
  });

  return {
    current,
    isLoading,
    load,
    update,
    reset,
  };
});
